
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ShoppingCart, 
  Heart, 
  Share2, 
  Minus, 
  Plus, 
  Truck, 
  Shield, 
  RotateCcw,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { useEnhancedCart } from "@/hooks/useEnhancedCart";
import { useToast } from "@/hooks/use-toast";
import { EnhancedProduct, ProductVariant, DiscountRule } from "@/types/product";
import WishlistButton from "@/components/WishlistButton";

interface EnhancedProductActionsProps {
  product: EnhancedProduct;
  selectedVariant?: ProductVariant | null;
  onBuyNow?: () => void;
  className?: string;
}

const EnhancedProductActions = ({
  product,
  selectedVariant,
  onBuyNow,
  className = ""
}: EnhancedProductActionsProps) => {
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { addToCartWithNotification } = useEnhancedCart();
  const { toast } = useToast();

  // Get effective product data (variant or main product)
  const effectiveProduct = selectedVariant || product;
  const availableStock = selectedVariant?.stock_quantity ?? product.stock_quantity ?? 0;
  const isOutOfStock = availableStock === 0;
  const isLowStock = availableStock > 0 && availableStock <= 5;
  const basePrice = selectedVariant?.price ?? product.price ?? 0;

  // Check if variant selection is required
  const hasVariants = product.variants && product.variants.length > 1;
  const requiresVariantSelection = hasVariants && !selectedVariant;

  // Calculate pricing with discounts
  const calculatePrice = () => {
    let finalPrice = basePrice;
    let appliedDiscount: DiscountRule | null = null;

    // Apply quantity discounts
    if (product.discount_rules) {
      const applicableDiscounts = product.discount_rules.filter(rule => 
        rule.rule_type === 'quantity' && quantity >= rule.min_quantity
      ).sort((a, b) => b.discount_value - a.discount_value);

      if (applicableDiscounts.length > 0) {
        appliedDiscount = applicableDiscounts[0];
        if (appliedDiscount.discount_type === 'percentage') {
          finalPrice = basePrice * (1 - appliedDiscount.discount_value / 100);
        } else {
          finalPrice = basePrice - appliedDiscount.discount_value;
        }
      }
    }

    return {
      originalPrice: basePrice,
      finalPrice: Math.max(finalPrice, 0),
      appliedDiscount,
      savings: basePrice - finalPrice,
      totalPrice: Math.max(finalPrice, 0) * quantity
    };
  };

  const pricing = calculatePrice();

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) return;
    if (newQuantity > availableStock) {
      toast({
        title: "Insufficient Stock",
        description: `Only ${availableStock} items available`,
        variant: "destructive",
      });
      return;
    }
    setQuantity(newQuantity);
  };

  const handleAddToCart = async (event?: React.MouseEvent) => {
    // Add to cart action triggered
    
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isOutOfStock) {
      // Product out of stock
      return;
    }
    
    // Check if variant selection is required
    if (requiresVariantSelection) {
      // Variant selection required
      toast({
        title: "Product Options Required",
        description: "Please select all product options before adding to cart",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    
    try {
      const productToAdd = selectedVariant ? {
        ...product,
        price: selectedVariant.price ?? product.price,
        stock_quantity: selectedVariant.stock_quantity ?? product.stock_quantity,
        variant_id: selectedVariant.id,
        variant_name: selectedVariant.name
      } : product;

      const success = await addToCartWithNotification(productToAdd, quantity);
      
      if (success) {
        // Reset quantity after successful add
        setQuantity(1);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyNow = async (event?: React.MouseEvent) => {
    // Buy now action triggered
    
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isOutOfStock) {
      // Product out of stock
      return;
    }
    
    // Check if variant selection is required
    if (requiresVariantSelection) {
      // Variant selection required
      toast({
        title: "Product Options Required",
        description: "Please select all product options before proceeding to checkout",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await handleAddToCart();
      if (onBuyNow) {
        onBuyNow();
      } else {
        // Navigate to checkout
        window.location.href = '/checkout';
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process purchase. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: product.description || '',
          url: window.location.href,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link Copied",
          description: "Product link copied to clipboard",
        });
      }
    } catch (error) {
      // Share cancelled or failed - no action needed
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Stock Status */}
      <div className="flex items-center gap-2">
        {isOutOfStock ? (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Out of Stock
          </Badge>
        ) : isLowStock ? (
          <Badge variant="secondary" className="flex items-center gap-1 text-orange-600 border-orange-200">
            <AlertTriangle className="h-3 w-3" />
            Only {availableStock} left in stock
          </Badge>
        ) : (
          <Badge variant="default" className="flex items-center gap-1 bg-green-100 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3" />
            In Stock ({availableStock} available)
          </Badge>
        )}
      </div>

      {/* Pricing */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-foreground">
                ${pricing.finalPrice.toFixed(2)}
              </div>
              {pricing.savings > 0 && (
                <div className="text-sm text-muted-foreground line-through">
                  ${pricing.originalPrice.toFixed(2)}
                </div>
              )}
            </div>
            {pricing.appliedDiscount && (
              <Badge variant="destructive" className="animate-pulse">
                {pricing.appliedDiscount.discount_type === 'percentage' ? 
                  `${pricing.appliedDiscount.discount_value}% OFF` :
                  `$${pricing.appliedDiscount.discount_value} OFF`
                }
              </Badge>
            )}
          </div>

          {pricing.savings > 0 && (
            <div className="text-sm text-green-600 font-medium">
              You save ${pricing.savings.toFixed(2)}!
            </div>
          )}

          <div className="text-lg font-semibold text-primary">
            Total: ${pricing.totalPrice.toFixed(2)}
          </div>
        </CardContent>
      </Card>

      {/* Quantity Selector */}
      {!isOutOfStock && (
        <div className="space-y-3">
          <Label htmlFor="quantity">Quantity</Label>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            
            <Input
              id="quantity"
              type="number"
              min="1"
              max={availableStock}
              value={quantity}
              onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
              className="w-20 text-center"
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(quantity + 1)}
              disabled={quantity >= availableStock}
            >
              <Plus className="h-4 w-4" />
            </Button>
            
            <span className="text-sm text-muted-foreground">
              of {availableStock} available
            </span>
          </div>
        </div>
      )}

      {/* Variant Selection Notice */}
      {requiresVariantSelection && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">Please select all product options to continue</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3">
          <Button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isLoading || requiresVariantSelection}
            size="lg"
            className={`w-full ${requiresVariantSelection ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}`}
            style={{ pointerEvents: 'auto' }}
          >
            {isLoading ? (
              "Adding..."
            ) : isOutOfStock ? (
              "Notify When Available"
            ) : requiresVariantSelection ? (
              "Select Product Options"
            ) : (
              <>
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart
              </>
            )}
          </Button>

          <Button
            onClick={handleBuyNow}
            disabled={isOutOfStock || isLoading || requiresVariantSelection}
            variant="secondary"
            size="lg"
            className={`w-full ${requiresVariantSelection ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}`}
            style={{ pointerEvents: 'auto' }}
          >
            {isOutOfStock ? "Out of Stock" : requiresVariantSelection ? "Select Product Options" : "Buy Now"}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <WishlistButton 
            productId={product.id} 
            variant="outline" 
            size="lg" 
            className="w-full"
          />
          
          <Button
            onClick={handleShare}
            variant="outline"
            size="lg"
            className="w-full"
            style={{ pointerEvents: 'auto' }}
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      {/* Trust Indicators */}
      <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center gap-2 text-blue-700">
              <Truck className="h-4 w-4" />
              <span>Free shipping on orders over $50</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <Shield className="h-4 w-4" />
              <span>Secure payment & buyer protection</span>
            </div>
            <div className="flex items-center gap-2 text-purple-700">
              <RotateCcw className="h-4 w-4" />
              <span>30-day return policy</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedProductActions;
